import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, CardContent, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, IconButton, Tooltip, TablePagination, Box, Typography,
  Tab, Tabs, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Divider, Grid,
} from '@mui/material';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import AddIcon from '@mui/icons-material/Add';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import Layout from '../../components/Layout/Layout';
import PageHeader from '../../components/Common/PageHeader';
import SearchFilterBar from '../../components/Common/SearchFilterBar';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import {
  getPublishersApi, togglePublisherStatusApi,
  getPublisherAgreementApi, updatePublisherAgreementApi,
  adminCreatePublisherApi,
  getPublisherNDAApi, updatePublisherNDAApi,
} from '../../api/auth';

function FileUploadField({ label, accept, value, onChange, error }) {
  return (
    <Box>
      <Typography variant="caption" color={error ? 'error' : 'text.secondary'} display="block" sx={{ mb: 0.5 }}>
        {label}
      </Typography>
      <Box
        component="label"
        sx={{
          display: 'flex', alignItems: 'center', gap: 1,
          border: '1px solid', borderColor: error ? 'error.main' : (value ? 'primary.main' : 'divider'),
          borderRadius: 1, px: 2, py: 1.2, cursor: 'pointer',
          bgcolor: value ? 'primary.50' : 'background.default',
          '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
          transition: 'all 0.15s',
        }}
      >
        {value
          ? <CheckCircleIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          : <UploadFileIcon sx={{ fontSize: 18, color: error ? 'error.main' : 'text.secondary' }} />}
        <Typography variant="body2" color={value ? 'primary.main' : (error ? 'error.main' : 'text.secondary')}
          sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
          {value ? value.name : 'Choose file...'}
        </Typography>
        <input type="file" accept={accept} onChange={onChange} style={{ display: 'none' }} />
      </Box>
    </Box>
  );
}

const emptyForm = {
  name: '', email: '', mobile_number: '', password: '', confirm_password: '',
  company_name: '', address: '', contact_person_name: '', designation: '', website_url: '',
  pan_number: '', partnership_date: '',
};
const emptyFiles = { company_address_proof: null, moa_document: null, gst_certificate: null };

export default function Publishers() {
  const [tab, setTab] = useState(0);
  const [publishers, setPublishers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [detail, setDetail] = useState(null);
  // Agreement
  const [agreementDialog, setAgreementDialog] = useState(null);
  const [agreementData, setAgreementData] = useState(null);
  const [agreementRemarks, setAgreementRemarks] = useState('');
  const [agreementSaving, setAgreementSaving] = useState(false);
  const [agreementMsg, setAgreementMsg] = useState('');
  // NDA
  const [ndaData, setNdaData] = useState(null);
  const [ndaRemarks, setNdaRemarks] = useState('');
  const [ndaSaving, setNdaSaving] = useState(false);
  const [ndaMsg, setNdaMsg] = useState('');
  // Add Publisher
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState(emptyForm);
  const [addFiles, setAddFiles] = useState(emptyFiles);
  const [addErrors, setAddErrors] = useState({});
  const [addNonFieldError, setAddNonFieldError] = useState('');
  const [addSuccess, setAddSuccess] = useState('');
  const [addLoading, setAddLoading] = useState(false);

  const fetchPublishers = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page: page + 1, page_size: rowsPerPage, search };
      const res = await getPublishersApi(params);
      setPublishers(res.data.results || res.data);
      setTotal(res.data.count || res.data.length);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, search]);

  useEffect(() => { fetchPublishers(); }, [fetchPublishers]);

  const handleToggle = async (id) => {
    await togglePublisherStatusApi(id);
    fetchPublishers();
    setConfirm(null);
  };

  const openAgreement = async (publisher) => {
    setAgreementDialog(publisher);
    setAgreementData(null);
    setAgreementRemarks('');
    setAgreementMsg('');
    setNdaData(null);
    setNdaRemarks('');
    setNdaMsg('');
    try {
      const res = await getPublisherAgreementApi(publisher.id);
      setAgreementData(res.data);
      setAgreementRemarks(res.data?.remarks || '');
    } catch {
      setAgreementData({ status: 'Pending' });
    }
    try {
      const res = await getPublisherNDAApi(publisher.id);
      setNdaData(res.data);
      setNdaRemarks(res.data?.remarks || '');
    } catch {
      setNdaData({ status: 'Pending' });
    }
  };

  const handleSaveAgreement = async (newStatus) => {
    if (!agreementDialog) return;
    setAgreementSaving(true);
    setAgreementMsg('');
    try {
      const fd = new FormData();
      fd.append('status', newStatus);
      if (agreementRemarks) fd.append('remarks', agreementRemarks);
      const res = await updatePublisherAgreementApi(agreementDialog.id, fd);
      setAgreementData(res.data);
      setAgreementMsg('Agreement updated successfully.');
      fetchPublishers();
    } catch (err) {
      setAgreementMsg(err.response?.data?.error || 'Failed to update agreement.');
    } finally {
      setAgreementSaving(false);
    }
  };

  const handleSaveNDA = async (newStatus) => {
    if (!agreementDialog) return;
    setNdaSaving(true);
    setNdaMsg('');
    try {
      const res = await updatePublisherNDAApi(agreementDialog.id, { status: newStatus, remarks: ndaRemarks });
      setNdaData(res.data);
      setNdaMsg('NDA Agreement updated successfully.');
      fetchPublishers();
    } catch (err) {
      setNdaMsg(err.response?.data?.error || 'Failed to update NDA agreement.');
    } finally {
      setNdaSaving(false);
    }
  };

  const openAddPublisher = () => {
    setAddForm(emptyForm);
    setAddFiles(emptyFiles);
    setAddErrors({});
    setAddNonFieldError('');
    setAddSuccess('');
    setAddOpen(true);
  };

  const handleAddPublisher = async (e) => {
    e.preventDefault();
    setAddLoading(true);
    setAddErrors({});
    setAddNonFieldError('');
    setAddSuccess('');
    const fd = new FormData();
    Object.entries(addForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
    Object.entries(addFiles).forEach(([k, v]) => { if (v) fd.append(k, v); });
    try {
      const res = await adminCreatePublisherApi(fd);
      setAddSuccess(res.data.message || 'Publisher created successfully.');
      fetchPublishers();
      setTimeout(() => { setAddOpen(false); setAddSuccess(''); }, 1500);
    } catch (err) {
      const data = err.response?.data || {};
      if (typeof data === 'string' || Array.isArray(data)) {
        setAddNonFieldError(String(data));
      } else if (data.non_field_errors) {
        setAddNonFieldError(data.non_field_errors[0]);
      } else {
        setAddErrors(data);
      }
    } finally {
      setAddLoading(false);
    }
  };

  const setF = (k) => (e) => setAddForm({ ...addForm, [k]: e.target.value });
  const setFile = (k) => (e) => setAddFiles({ ...addFiles, [k]: e.target.files[0] || null });

  const filteredPublishers = tab === 0
    ? publishers
    : publishers.filter((p) => !p.is_active);

  return (
    <Layout title="Publishers">
      <PageHeader
        title="Publishers"
        breadcrumbs={[{ label: 'Admin' }, { label: 'Publishers' }]}
        action={
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAddPublisher}>
            Add Publisher
          </Button>
        }
      />

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label={`All Publishers (${total})`} />
        <Tab label="Inactive" />
      </Tabs>

      <Card>
        <CardContent>
          <SearchFilterBar searchPlaceholder="Search by name, email, company..." onSearch={setSearch} />
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Mobile</TableCell>
                <TableCell>Company</TableCell>
                <TableCell>Total Books</TableCell>
                <TableCell>Agreement</TableCell>
                <TableCell>NDA</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredPublishers.map((p) => (
                <TableRow key={p.id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.name}</Typography>
                  </TableCell>
                  <TableCell>{p.email}</TableCell>
                  <TableCell>{p.mobile_number}</TableCell>
                  <TableCell>{p.company_name}</TableCell>
                  <TableCell>{p.total_books}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.agreement_status || 'Pending'}
                      size="small"
                      color={p.agreement_status === 'Approved' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={p.nda_status || 'Pending'}
                      size="small"
                      color={p.nda_status === 'Approved' ? 'success' : 'warning'}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip label={p.is_active ? 'Active' : 'Inactive'}
                      color={p.is_active ? 'success' : 'default'} size="small" />
                  </TableCell>
                  <TableCell>
                    <Tooltip title={p.is_active ? 'Deactivate' : 'Activate'}>
                      <IconButton size="small" color={p.is_active ? 'error' : 'success'}
                        onClick={() => setConfirm({ id: p.id, active: p.is_active, name: p.name })}>
                        {p.is_active ? <ToggleOffIcon /> : <ToggleOnIcon />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Manage Agreements">
                      <IconButton size="small" color="primary" onClick={() => openAgreement(p)}>
                        <AssignmentIcon />
                      </IconButton>
                    </Tooltip>
                    <Button size="small" onClick={() => setDetail(p)}>View</Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div" count={total} page={page} rowsPerPage={rowsPerPage}
            onPageChange={(_, p) => setPage(p)}
            onRowsPerPageChange={(e) => { setRowsPerPage(+e.target.value); setPage(0); }}
            rowsPerPageOptions={[10, 25, 50]}
          />
        </CardContent>
      </Card>

      {/* Toggle status confirm */}
      <ConfirmDialog
        open={!!confirm}
        title={confirm?.active ? 'Deactivate Publisher' : 'Activate Publisher'}
        message={`Are you sure you want to ${confirm?.active ? 'deactivate' : 'activate'} ${confirm?.name}?`}
        onConfirm={() => handleToggle(confirm?.id)}
        onCancel={() => setConfirm(null)}
        confirmText={confirm?.active ? 'Deactivate' : 'Activate'}
      />

      {/* Publisher detail dialog */}
      <Dialog open={!!detail} onClose={() => setDetail(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{detail?.name} — Publisher Details</DialogTitle>
        <DialogContent dividers>
          {detail && [
            ['Email', detail.email],
            ['Mobile', detail.mobile_number],
            ['Company', detail.company_name],
            ['Contact Person', detail.contact_person_name],
            ['Designation', detail.designation],
            ['Website', detail.website_url],
            ['Total Books', detail.total_books],
            ['Agreement', detail.agreement_status || 'Pending'],
            ['NDA Agreement', detail.nda_status || 'Pending'],
            ['Status', detail.is_active ? 'Active' : 'Inactive'],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', gap: 2, mb: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, width: 140, flexShrink: 0 }}>{k}:</Typography>
              <Typography variant="body2">{v || '—'}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { openAgreement(detail); setDetail(null); }} startIcon={<AssignmentIcon />}>
            Manage Agreements
          </Button>
          <Button onClick={() => setDetail(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Agreement & NDA dialog */}
      <Dialog open={!!agreementDialog} onClose={() => setAgreementDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Manage Agreements — {agreementDialog?.name}</DialogTitle>
        <DialogContent dividers>

          {/* Onboarding Agreement section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
            Onboarding Agreement
          </Typography>
          {agreementData && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Current Status:</Typography>
                <Chip
                  label={agreementData.status || 'Pending'}
                  color={agreementData.status === 'Approved' ? 'success' : 'warning'}
                />
              </Box>
              {agreementData.approval_date && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approved on: {new Date(agreementData.approval_date).toLocaleDateString()}
                  {agreementData.approved_by_name && ` by ${agreementData.approved_by_name}`}
                </Typography>
              )}
              <TextField
                fullWidth label="Remarks" multiline rows={2}
                value={agreementRemarks} onChange={(e) => setAgreementRemarks(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              {agreementMsg && (
                <Alert severity={agreementMsg.includes('success') ? 'success' : 'error'} sx={{ mb: 1 }}>
                  {agreementMsg}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
                <Button onClick={() => handleSaveAgreement('Pending')} disabled={agreementSaving} color="warning" variant="outlined" size="small">
                  Set Pending
                </Button>
                <Button onClick={() => handleSaveAgreement('Approved')} disabled={agreementSaving} color="success" variant="contained" size="small" startIcon={<CheckCircleIcon />}>
                  {agreementSaving ? 'Saving...' : 'Approve'}
                </Button>
              </Box>
            </>
          )}

          <Divider sx={{ my: 2 }} />

          {/* NDA Agreement section */}
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5, color: 'primary.main' }}>
            NDA Agreement
          </Typography>
          {ndaData && (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Current Status:</Typography>
                <Chip
                  label={ndaData.status || 'Pending'}
                  color={ndaData.status === 'Approved' ? 'success' : 'warning'}
                />
              </Box>
              {ndaData.approval_date && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Approved on: {new Date(ndaData.approval_date).toLocaleDateString()}
                  {ndaData.approved_by_name && ` by ${ndaData.approved_by_name}`}
                </Typography>
              )}
              <TextField
                fullWidth label="NDA Remarks" multiline rows={2}
                value={ndaRemarks} onChange={(e) => setNdaRemarks(e.target.value)}
                sx={{ mb: 1.5 }}
              />
              {ndaMsg && (
                <Alert severity={ndaMsg.includes('success') ? 'success' : 'error'} sx={{ mb: 1 }}>
                  {ndaMsg}
                </Alert>
              )}
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button onClick={() => handleSaveNDA('Pending')} disabled={ndaSaving} color="warning" variant="outlined" size="small">
                  Set Pending
                </Button>
                <Button onClick={() => handleSaveNDA('Approved')} disabled={ndaSaving} color="success" variant="contained" size="small" startIcon={<CheckCircleIcon />}>
                  {ndaSaving ? 'Saving...' : 'Approve NDA'}
                </Button>
              </Box>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAgreementDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Add Publisher dialog */}
      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New Publisher</DialogTitle>
        <DialogContent dividers>
          {addNonFieldError && <Alert severity="error" sx={{ mb: 2 }}>{addNonFieldError}</Alert>}
          {addSuccess && <Alert severity="success" sx={{ mb: 2 }}>{addSuccess}</Alert>}

          <Box component="form" id="add-publisher-form" onSubmit={handleAddPublisher} noValidate>

            {/* Personal Information */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700 }}>
              Personal Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Full Name" value={addForm.name} onChange={setF('name')}
                  required error={!!addErrors.name} helperText={addErrors.name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Email ID" type="email" value={addForm.email} onChange={setF('email')}
                  required error={!!addErrors.email} helperText={addErrors.email?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Mobile Number" value={addForm.mobile_number} onChange={setF('mobile_number')}
                  required error={!!addErrors.mobile_number} helperText={addErrors.mobile_number?.[0]}
                  inputProps={{ maxLength: 15 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Password" type="password" value={addForm.password} onChange={setF('password')}
                  required error={!!addErrors.password} helperText={addErrors.password?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Confirm Password" type="password" value={addForm.confirm_password}
                  onChange={setF('confirm_password')} required error={!!addErrors.confirm_password}
                  helperText={addErrors.confirm_password?.[0]} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* Company Information */}
            <Typography variant="subtitle2" sx={{ mb: 1.5, color: 'primary.main', fontWeight: 700 }}>
              Company Information
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Company Name" value={addForm.company_name} onChange={setF('company_name')}
                  required error={!!addErrors.company_name} helperText={addErrors.company_name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Contact Person Name" value={addForm.contact_person_name}
                  onChange={setF('contact_person_name')} error={!!addErrors.contact_person_name}
                  helperText={addErrors.contact_person_name?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Designation" value={addForm.designation} onChange={setF('designation')}
                  error={!!addErrors.designation} helperText={addErrors.designation?.[0]} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Website URL" value={addForm.website_url} onChange={setF('website_url')}
                  error={!!addErrors.website_url} helperText={addErrors.website_url?.[0]}
                  placeholder="https://example.com" />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Company Address" multiline rows={2} value={addForm.address}
                  onChange={setF('address')} error={!!addErrors.address} helperText={addErrors.address?.[0]} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 2.5 }} />

            {/* Company Documents */}
            <Box sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'primary.main', fontWeight: 700 }}>
                Company Documents / Details
              </Typography>
              <Chip label="At least one required" size="small" color="warning" />
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
              Provide at least one: Address Proof, MOA, PAN Number, GST Certificate, or Partnership Date.
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="Company Address Proof (PDF / Image)"
                  accept=".pdf,.jpg,.jpeg,.png"
                  value={addFiles.company_address_proof}
                  onChange={setFile('company_address_proof')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="Memorandum of Association (MOA)"
                  accept=".pdf,.doc,.docx"
                  value={addFiles.moa_document}
                  onChange={setFile('moa_document')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="PAN Number" value={addForm.pan_number} onChange={setF('pan_number')}
                  error={!!addErrors.pan_number} helperText={addErrors.pan_number?.[0]}
                  inputProps={{ maxLength: 20 }} />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FileUploadField
                  label="GST Registration Certificate (PDF / Image)"
                  accept=".pdf,.jpg,.jpeg,.png"
                  value={addFiles.gst_certificate}
                  onChange={setFile('gst_certificate')}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField fullWidth label="Partnership Date" type="date" value={addForm.partnership_date}
                  onChange={setF('partnership_date')} error={!!addErrors.partnership_date}
                  helperText={addErrors.partnership_date?.[0] || 'If applicable'}
                  InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button type="submit" form="add-publisher-form" variant="contained" disabled={addLoading}>
            {addLoading ? 'Creating...' : 'Create Publisher'}
          </Button>
        </DialogActions>
      </Dialog>
    </Layout>
  );
}
